/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BaseError } from '../shared/errors/Errors';
import {
	ClientCommunity,
	ClientGeoArea,
	ClientGeoAreaExtended,
	ClientPersonChangeEmailAddressRequest,
	ClientPersonOwn,
	ClientRoleInformation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { RoleManagementPermission } from '../shared/services/Role.service';

export const REFRESH_GLOBAL_STATE_REQUEST = 'REFRESH_GLOBAL_STATE_REQUEST';
export const REFRESH_GLOBAL_STATE_SUCCESS = 'REFRESH_GLOBAL_STATE_SUCCESS';
export const REFRESH_GLOBAL_STATE_FAILURE = 'REFRESH_GLOBAL_STATE_FAILURE';

export const GET_GEO_AREA_REQUEST = 'GET_GEO_AREA_REQUEST';
export const GET_GEO_AREA_SUCCESS = 'GET_GEO_AREA_SUCCESS';
export const GET_GEO_AREA_FAILURE = 'GET_GEO_AREA_FAILURE';

export const GET_GEO_AREAS_REQUEST = 'GET_GEO_AREAS_REQUEST';
export const GET_GEO_AREAS_SUCCESS = 'GET_GEO_AREAS_SUCCESS';
export const GET_GEO_AREAS_FAILURE = 'GET_GEO_AREAS_FAILURE';

export const CHANGE_EMAIL_ADDRESS_REQUEST = 'CHANGE_EMAIL_ADDRESS_REQUEST';
export const CHANGE_EMAIL_ADDRESS_SUCCESS = 'CHANGE_EMAIL_ADDRESS_SUCCESS';
export const CHANGE_EMAIL_ADDRESS_FAILURE = 'CHANGE_EMAIL_ADDRESS_FAILURE';

export interface RefreshGlobalStateRequestAction {
	type: typeof REFRESH_GLOBAL_STATE_REQUEST;
}

export interface RefreshGlobalStateSuccessAction {
	type: typeof REFRESH_GLOBAL_STATE_SUCCESS;
	tenants: ClientCommunity[];
	allAvailableRoles: ClientRoleInformation[];
	assignableRoles: ClientRoleInformation[];
	roleManagementPermission: RoleManagementPermission;
	person: ClientPersonOwn;
}

export interface RefreshGlobalStateFailureAction {
	type: typeof REFRESH_GLOBAL_STATE_FAILURE;
	error: BaseError | null;
}

export interface GetGeoAreaRequestAction {
	type: typeof GET_GEO_AREA_REQUEST;
	geoAreaId: string;
}

export interface GetGeoAreaSuccessAction {
	type: typeof GET_GEO_AREA_SUCCESS;
	geoArea: ClientGeoArea;
}

export interface GetGeoAreaFailureAction {
	type: typeof GET_GEO_AREA_FAILURE;
	error: BaseError | null;
}

export interface GetGeoAreasRequestAction {
	type: typeof GET_GEO_AREAS_REQUEST;
}

export interface GetGeoAreasSuccessAction {
	type: typeof GET_GEO_AREAS_SUCCESS;
	geoAreas: ClientGeoAreaExtended[];
}

export interface GetGeoAreasFailureAction {
	type: typeof GET_GEO_AREAS_FAILURE;
	error: BaseError | null;
}

export interface ChangeEmailAddressRequestAction {
	type: typeof CHANGE_EMAIL_ADDRESS_REQUEST;
	changeEmailAddressRequestParams: ClientPersonChangeEmailAddressRequest;
}
export interface ChangeEmailAddressSuccessAction {
	type: typeof CHANGE_EMAIL_ADDRESS_SUCCESS;
	person: ClientPersonOwn;
}

export interface ChangeEmailAddressFailureAction {
	type: typeof CHANGE_EMAIL_ADDRESS_FAILURE;
	error: BaseError | null;
}

export type GlobalActionTypes =
	| RefreshGlobalStateRequestAction
	| RefreshGlobalStateSuccessAction
	| RefreshGlobalStateFailureAction
	| GetGeoAreaRequestAction
	| GetGeoAreaSuccessAction
	| GetGeoAreaFailureAction
	| GetGeoAreasRequestAction
	| GetGeoAreasSuccessAction
	| GetGeoAreasFailureAction
	| ChangeEmailAddressRequestAction
	| ChangeEmailAddressSuccessAction
	| ChangeEmailAddressFailureAction;
